import {
  login as apiLogin,
  logout as apiLogout,
  refreshToken,
} from "@/api/auth";
import {
  getToken,
  getRefreshToken,
  setToken,
  setRefreshToken,
  removeToken,
  removeRefreshToken,
  getUsername,
  setUsername,
  removeUsername,
} from "@/utils/auth";
import { resetRouter } from "@/router";

function decodeRolesFromToken(token) {
  if (!token) return [];
  try {
    const parts = token.split(".");
    if (parts.length < 2) return [];
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(jsonPayload);
    if (Array.isArray(payload.roles)) {
      return payload.roles;
    }
    if (typeof payload.role === "string") {
      return [payload.role];
    }
    return [];
  } catch (e) {
    return [];
  }
}

const getDefaultState = () => ({
  token: getToken(),
  refresh_token: getRefreshToken(),
  roles: [],
  username: getUsername() || null,
});

const state = getDefaultState();

const mutations = {
  RESET_STATE(state) {
    Object.assign(state, getDefaultState());
  },
  SET_TOKEN(state, token) {
    state.token = token;
  },
  SET_REFRESH_TOKEN(state, token) {
    state.refresh_token = token;
  },
  SET_ROLES(state, roles) {
    state.roles = roles || [];
  },
  SET_USERNAME(state, username) {
    state.username = username;
  },
};

const actions = {
  async login({ commit }, userInfo) {
    const { username, password, grant_type } = userInfo;
    const data = {
      username: username.trim(),
      password,
      grant_type: grant_type || "password",
    };

    const resp = await apiLogin(data);
    commit("SET_TOKEN", resp.access_token);
    commit("SET_REFRESH_TOKEN", resp.refresh_token);
    setToken(resp.access_token);
    setRefreshToken(resp.refresh_token);
    setUsername(username.trim());
    commit("SET_USERNAME", username.trim());

    const roles = decodeRolesFromToken(resp.access_token);
    commit("SET_ROLES", roles);

    return resp;
  },

  async refresh({ commit }) {
    const currentRefresh = getRefreshToken();
    if (!currentRefresh) return;

    const resp = await refreshToken(currentRefresh);
    commit("SET_TOKEN", resp.access_token);
    commit("SET_REFRESH_TOKEN", resp.refresh_token);
    setToken(resp.access_token);
    setRefreshToken(resp.refresh_token);

    const roles = decodeRolesFromToken(resp.access_token);
    commit("SET_ROLES", roles);

    return resp;
  },

  async logout({ commit }) {
    try {
      await apiLogout();
    } finally {
      removeToken();
      removeRefreshToken();
      removeUsername();
      resetRouter();
      commit("RESET_STATE");
    }
  },

  resetToken({ commit }) {
    removeToken();
    removeRefreshToken();
    removeUsername();
    commit("RESET_STATE");
  },

  async getInfo({ commit }) {
    const token = getToken();
    if (!token) return;
    const roles = decodeRolesFromToken(token);
    commit("SET_ROLES", roles);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
